import styled from 'styled-components';
import { Button } from '@repo/ui';
import React, {useState} from 'react';
import LanguageChange from '@/components/Menu/shared/LanguageChange';
import {BREAKPOINTS, COLORS, THEME_MODES} from "@repo/ui/themeSettings";
import ThemeSwitcher from "@/components/Menu/shared/ThemeSwitcher";
import { FontSizeChangeCrossCommon } from "@repo/ui/SVGR";
import { CURSOR_TYPES } from '@repo/utils/constants';
import { useDispatch } from "react-redux";
import { setIsMenuOpen, setSelectedItem, setSelectedSubItem } from "@/state/menuSlice";
import { useRouter } from "next/router";
import { Modal, revealAnimationContainer } from "@repo/ui";
import {getCssSizeValue} from "@repo/utils";
import {setIsModalVisible, setModalData} from "@/state/modalSlice";


const StyledBottomRow = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
        margin-top: ${getCssSizeValue(145, BREAKPOINTS.DESKTOP)};
    }

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        margin-top: unset;
    }
`;

const StyledContactInfoWrapper = styled.div`
    display: flex;
    gap: 50px;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
        align-items: flex-end;
    }
`;

const StyledSingleContactInfoItem = styled.div`
    display: none;

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        color: ${({ theme }) => (theme.mode === THEME_MODES.DARK ? theme.colors.white : theme.colors.twilightBlue)};
        display: flex;
        flex-direction: column;
    }
`;

export const StyledHeader = styled(revealAnimationContainer('div', 'menu-bottom-row-item-reveal-up'))`
    font-size: 12px;
    flex: 1;
    color: ${({ theme }) => theme.colors.white};
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
        font-size: 14px;
    }

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        font-size: 12px;
        color: ${({ theme }) => (theme.mode === THEME_MODES.DARK ? theme.colors.white : theme.colors.twilightBlue)};
    }
`;

export const StyledAddress = styled(revealAnimationContainer('div', 'menu-bottom-row-item-reveal-up'))`
    font-size: 16px;
    font-style: italic;
    color: ${({ theme }) => theme.colors.white};
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
        font-size: 18px;
    }
    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        font-size: 16px;
        color: ${({ theme }) => (theme.mode === THEME_MODES.DARK ? theme.colors.white : theme.colors.twilightBlue)};
    }
`;

export const StyledText = styled(revealAnimationContainer('div', 'menu-bottom-row-item-reveal-up'))`
    &, a {
        font-size: 16px;
        font-style: italic;
        text-decoration: underline;
        color: ${({ theme }) => theme.colors.white};
    
        &.gap-top {
            margin-top: 10px;
        }
    
        @media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
            font-size: 18px;
            &.gap-top {
                margin-top: unset;
            }
        }
    
        @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
            color: ${({ theme }) => (theme.mode === THEME_MODES.DARK ? theme.colors.white : theme.colors.twilightBlue)};
            text-decoration: none;
            font-size: 16px;
        }
    }
`;

export const StyledSingleSocialItemReveal = styled(revealAnimationContainer('div', 'menu-bottom-row-item-reveal-up'))``

export const StyledSingleSocialItem = styled.a`
    color: ${({ theme }) => theme.colors.white};
    display: flex;
    text-align: end;
    justify-content: flex-end;
    font-size: 16px;
    font-style: italic;
    text-transform: uppercase;
    text-decoration: none;

    transition: color var(--color-transition-duration) var(--ease);
    position: relative;

    &.active {
        color: var(--tertiary-color);
    }

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        color: ${({theme}) => (theme.mode === THEME_MODES.DARK ? theme.colors.white : theme.colors.twilightBlue)};
        display: block;
        justify-content: flex-start;
        text-align: start;
        width: fit-content;
    }
`;

export const SocialsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    flex: 1;
    gap: 10px;
    max-width: 80%;

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
        display: flex;
        justify-content: initial;
        flex-wrap: initial;
        flex: initial;
        gap: 14px;
        max-width: unset;
    }

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        display: grid;
        gap: unset;
        column-gap: 10px;
        grid-template-columns: ${({ $colsNumber }) => `repeat(${$colsNumber}, auto)`}
    }
`;

const StyledSettingsWrapper = styled.div`
    display: flex;
    gap: 15px;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
        gap: 25px;
    }

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        flex-direction: column-reverse;
        gap: unset;
        align-items: flex-end;
    }
`;

const RevealUpButtonContainer = revealAnimationContainer('div', 'menu-bottom-row-item-reveal-up')

const StyledIconWrapper = styled.div`
    height: 8px;
    width: 8px;
    transition: all .4s;
`

const StyledFontSizeChange = styled(revealAnimationContainer('a', 'menu-bottom-row-settings-reveal-up'))`
    font-size: 18px;
    font-weight: 700;
    height: 100%;
    display: flex;
    gap: 6px;
    align-items: center;
    overflow: hidden;
    padding-left: 4px;
    margin-bottom: 2px;

    &:hover {
        ${StyledIconWrapper} {
            transform: scale(1.4);
        }
    }

    @media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
        margin-bottom: 5px;
    }
`;

const StyledIconsWrapper = styled.div`
    display: flex;
    gap: 4px;
`

const BottomRow = ({ data, globals }) => {
    const dispatch = useDispatch()
    const { push, asPath, locale } = useRouter();
    const menuItems = data?.menuItems || [];
    const lastNavItem = menuItems[menuItems.length - 1] || null;
    const slug = lastNavItem?.link?.slug || '';
    const text = lastNavItem?.link?.menuContext?.previewText || '';

    const contactHeader = data?.contactHeader || '';
    const contactEmail = globals?.email || '';
    const contactPhoneNumber = globals?.phone || '';

    const socialsHeader = data?.socialsHeader || '';
    const socialsItems = globals?.socials || [];

    const modalHeader = globals?.modalHeader || ''
    const modalDescription = globals?.modalDescription || '';
    const modalButtonTextClose = globals?.buttonTextClose || '';
    const modalButtonTextOpen = globals?.buttonTextOpen || '';
    const handleOnClick = () => {
        dispatch(setSelectedSubItem(null))
        dispatch(setModalData({
            header: modalHeader,
            description: modalDescription,
            closeButtonText: modalButtonTextClose,
            openButtonText: modalButtonTextOpen,
            externalUrl: `${process.env.PLEO_GROUP_URL}${locale=== 'pl' ? '/pl/' :'/'}news`
        }))
        dispatch(setIsModalVisible(true))
    };
    return (
        <StyledBottomRow>
            <StyledContactInfoWrapper>
                <RevealUpButtonContainer>
                    <Button variant="tertiary" onClick={handleOnClick} onMouseEnter={() => dispatch(setSelectedItem(lastNavItem))}>{text}</Button>
                </RevealUpButtonContainer>
                <StyledSingleContactInfoItem>
                    <StyledHeader>{contactHeader}</StyledHeader>
                    <StyledText>
                        <a href={`mailto: ${contactEmail}`}>
                            {contactEmail}
                        </a>
                    </StyledText>
                    <StyledText>
                        <a href={`tel:${contactPhoneNumber}`}>
                            {contactPhoneNumber}
                        </a>
                    </StyledText>
                </StyledSingleContactInfoItem>
                <StyledSingleContactInfoItem>
                    <StyledHeader>{socialsHeader}</StyledHeader>
                    <SocialsWrapper $colsNumber={Math.round(socialsItems.length / 2)}>
                        {socialsItems.map((item, index) => (
                            <StyledSingleSocialItem href={item?.link || ''} target="_blank" className={`underline-animation`} data-cursor="link" key={item?.id || index}>
                                <StyledSingleSocialItemReveal >
                                    {item.title}
                                </StyledSingleSocialItemReveal>
                            </StyledSingleSocialItem>
                        ))}
                    </SocialsWrapper>
                </StyledSingleContactInfoItem>
            </StyledContactInfoWrapper>

            <StyledSettingsWrapper>
                {/*<LanguageChange />*/}
                {/*<StyledFontSizeChange data-cursor={CURSOR_TYPES.ROUNDED_OUTLINE}>*/}
                {/*    <StyledIconsWrapper>*/}
                {/*        <StyledIconWrapper>*/}
                {/*            <FontSizeChangeCrossCommon />*/}
                {/*        </StyledIconWrapper>*/}
                {/*        <StyledIconWrapper>*/}
                {/*            <FontSizeChangeCrossCommon />*/}
                {/*        </StyledIconWrapper>*/}
                {/*    </StyledIconsWrapper>*/}
                {/*    A*/}
                {/*</StyledFontSizeChange>*/}
                {/*<ThemeSwitcher />*/}
            </StyledSettingsWrapper>
        </StyledBottomRow >
    );
};

export default BottomRow;
